import { APP_PATH, APP_PATH_PREFIX } from '../shared/app-path.js'
import { recordBounce } from './bounce-guard.js'

// Re-exported so existing importers (and the cross-app contract) keep a single, stable home for the
// dashboard base path even though its literal now lives in the shared module.
export { APP_PATH }

/**
 * Master gate for the dashboard handoff. Enabled now that the `/app` dashboard v1 VN variant is
 * deployed and durably served from the shared delivery origin (step 9b-4): dashboard `deploy-vn.yml`
 * publishes the CRA build under `/app/*`, and the vn-pwa shell deploy no longer prunes that subtree
 * (its root `--delete` excludes `app/*`, tsar #513), so `/app` genuinely serves the dashboard
 * document rather than falling back to this shell.
 *
 * WHY the gate exists at all: while `/app` was undeployed, CloudFront's SPA-fallback returned THIS
 * install shell for `/app`. Navigating there re-booted the shell, which found the same bearer session
 * in IndexedDB, re-entered the `authenticated` view, and navigated to `/app` again — an infinite
 * ~300ms navigation loop that presented as a flickering install/blank screen (PT-5598). {@link
 * isUnderAppPath} remains the second line of defence against that self-loop even with the gate on.
 */
export const APP_HANDOFF_ENABLED = true

/**
 * True when the current path is already the dashboard base or a route below it. The handoff must
 * never navigate to `/app` from within `/app`: even once {@link APP_HANDOFF_ENABLED} is on, a stale
 * SPA-fallback (or a slow dashboard rollout) could otherwise re-trigger the same self-loop.
 */
export const isUnderAppPath = (pathname: string): boolean =>
  pathname === APP_PATH || pathname.startsWith(APP_PATH_PREFIX)

/** The outcome of an attempted handoff, so the caller can render a fallback when the loop is stuck. */
export type HandoffResult = 'exhausted' | 'redirected' | 'skipped'

/**
 * Hand a freshly-authenticated session over to the dashboard. A same-origin navigation (not an
 * in-place render) so the dashboard boots as its own document at `/app`, reading the shared bearer
 * token and API domain. `location`, `now` and `storage` are injected for tests; production reads the
 * globals.
 *
 * No-op (`'skipped'`) while the handoff is dark or when we are already under `/app`, so an
 * authenticated session can never drive the navigation loop described on {@link APP_HANDOFF_ENABLED}.
 *
 * Bounded against the *other* non-converging loop (PT-5648): if storage is permanently blocked or the
 * API host is permanently broken, the shell and the dashboard would bounce between `/` and `/app`
 * forever, each side re-discovering the same dead state. {@link recordBounce} caps the shared hop
 * counter — once spent, this returns `'exhausted'` without navigating so the caller can show a
 * user-visible fallback instead.
 */
export const redirectToApp = ({
  location = window.location,
  now = Date.now(),
  storage,
}: {
  location?: Pick<Location, 'assign' | 'pathname'>
  now?: number
  storage?: Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>
} = {}): HandoffResult => {
  if (!APP_HANDOFF_ENABLED) return 'skipped'
  if (isUnderAppPath(location.pathname)) return 'skipped'
  if (recordBounce(now, storage) === 'exhausted') return 'exhausted'
  location.assign(APP_PATH)
  return 'redirected'
}
